import React from "react";
import { Link } from "react-router-dom";
import {
  Gamepad2,
  Users,
  TrendingUp,
  Link as LinkIcon,
  DollarSign,
  BarChart3,
  ArrowRight,
  ChevronRight,
  Zap,
  Shield,
  Globe,
  Linkedin,
  Twitter,
} from "lucide-react";

function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Gamepad2 className="w-8 h-8 text-brand-400" />
              <span className="text-xl font-bold bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
                referred.games
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors">
                How It Works
              </a>
              <a href="#publishers" className="text-sm text-gray-400 hover:text-white transition-colors">
                For Publishers
              </a>
              <a href="#influencers" className="text-sm text-gray-400 hover:text-white transition-colors">
                For Influencers
              </a>
              <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                Contact
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/auth?mode=login"
                className="text-sm text-gray-300 hover:text-white transition-colors px-4 py-2"
              >
                Sign In
              </Link>
              <Link
                to="/auth?mode=signup"
                className="text-sm bg-brand-600 hover:bg-brand-500 text-white px-5 py-2 rounded-lg transition-colors font-medium"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/20 via-gray-950 to-gray-950" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-brand-500/5 rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-brand-500/10 border border-brand-500/20 rounded-full px-4 py-1.5 mb-8">
            <Zap className="w-4 h-4 text-brand-400" />
            <span className="text-sm text-brand-300">Performance-based influencer marketing for games</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            Connect Games with
            <br />
            <span className="bg-gradient-to-r from-brand-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Influencers That Convert
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Publishers pay per wishlist. Influencers earn per conversion.
            Track everything with Steam UTM links — no guesswork, just results.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/auth?mode=signup&role=publisher"
              className="group flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-8 py-3.5 rounded-xl transition-all font-semibold text-lg shadow-lg shadow-brand-600/25 hover:shadow-brand-500/30"
            >
              I'm a Publisher
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/auth?mode=signup&role=influencer"
              className="group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-8 py-3.5 rounded-xl transition-all font-semibold text-lg border border-white/10 hover:border-white/20"
            >
              I'm an Influencer
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Logos / Social Proof */}
      <section className="py-12 border-y border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500 mb-8 uppercase tracking-wider">
            Built for the gaming ecosystem
          </p>
          <div className="flex items-center justify-center gap-12 flex-wrap opacity-40">
            <span className="text-2xl font-bold text-gray-400">Steam</span>
            <span className="text-2xl font-bold text-gray-400">YouTube</span>
            <span className="text-2xl font-bold text-gray-400">Twitch</span>
            <span className="text-2xl font-bold text-gray-400">TikTok</span>
            <span className="text-2xl font-bold text-gray-400">Twitter</span>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Simple, transparent, and performance-driven
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                icon: <Gamepad2 className="w-6 h-6" />,
                title: "Publisher Creates Campaign",
                desc: "Set your game, budget, and cost-per-wishlist. Define exactly what you'll pay for real results.",
              },
              {
                step: "02",
                icon: <LinkIcon className="w-6 h-6" />,
                title: "Influencers Join & Get UTM Links",
                desc: "Influencers browse campaigns, join ones they like, and get unique Steam UTM tracking links.",
              },
              {
                step: "03",
                icon: <DollarSign className="w-6 h-6" />,
                title: "Track Wishlists & Earn",
                desc: "Publishers upload Steam analytics CSVs weekly. Influencers see their conversions and earnings in real-time.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="group relative bg-gray-900/50 border border-gray-800/50 rounded-2xl p-8 hover:border-brand-500/30 transition-all hover:bg-gray-900/80"
              >
                <div className="text-5xl font-black text-gray-800/50 absolute top-6 right-8">
                  {item.step}
                </div>
                <div className="w-12 h-12 bg-brand-500/10 rounded-xl flex items-center justify-center text-brand-400 mb-5">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Publishers */}
      <section id="publishers" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-6">
                <Gamepad2 className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-300">For Publishers</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Pay Only for
                <br />
                <span className="text-blue-400">Real Wishlists</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                No more flat-rate influencer deals with no accountability.
                Set your price per wishlist, and only pay when players actually add your game.
              </p>
              <ul className="space-y-4">
                {[
                  "Set custom cost-per-wishlist rates",
                  "Upload Steam UTM analytics weekly via CSV",
                  "Full transparency on influencer performance",
                  "Control your budget with campaign caps",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <ChevronRight className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-gray-800">
                  <span className="text-gray-400">Campaign</span>
                  <span className="font-semibold">Starfield Explorer</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-800">
                  <span className="text-gray-400">Cost per Wishlist</span>
                  <span className="font-semibold text-green-400">$1.00</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-800">
                  <span className="text-gray-400">Total Wishlists</span>
                  <span className="font-semibold">12,847</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-800">
                  <span className="text-gray-400">Active Influencers</span>
                  <span className="font-semibold">34</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Budget Spent</span>
                  <span className="font-semibold text-brand-400">$12,847</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Influencers */}
      <section id="influencers" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 bg-gray-900 border border-gray-800 rounded-2xl p-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-gray-800/50 rounded-xl p-4">
                  <div>
                    <div className="font-semibold">Cyber Odyssey</div>
                    <div className="text-sm text-gray-400">RPG • Action</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-semibold">$1.50/wishlist</div>
                    <div className="text-sm text-gray-400">847 wishlists</div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-800/50 rounded-xl p-4">
                  <div>
                    <div className="font-semibold">Farm Valley</div>
                    <div className="text-sm text-gray-400">Simulation</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-semibold">$0.75/wishlist</div>
                    <div className="text-sm text-gray-400">2,103 wishlists</div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-800/50 rounded-xl p-4">
                  <div>
                    <div className="font-semibold">Shadow Wars</div>
                    <div className="text-sm text-gray-400">Strategy</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-semibold">$2.00/wishlist</div>
                    <div className="text-sm text-gray-400">412 wishlists</div>
                  </div>
                </div>
                <div className="border-t border-gray-700 pt-4 flex items-center justify-between">
                  <span className="text-gray-400">Total Earnings</span>
                  <span className="text-2xl font-bold text-green-400">$3,671.75</span>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-6">
                <Users className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-300">For Influencers</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Earn for Every
                <br />
                <span className="text-purple-400">Wishlist You Drive</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Browse campaigns, pick games you love, share your unique link,
                and earn money for every wishlist your audience generates.
              </p>
              <ul className="space-y-4">
                {[
                  "Browse and join campaigns that fit your audience",
                  "Get auto-generated Steam UTM tracking links",
                  "Track your wishlists and earnings in real-time",
                  "Get paid based on actual performance",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why referred.games?</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Built specifically for the gaming industry
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: "Performance Based",
                desc: "Pay and earn based on actual wishlists — real results, not impressions.",
              },
              {
                icon: <LinkIcon className="w-6 h-6" />,
                title: "Automatic UTM Links",
                desc: "Every influencer gets a unique Steam tracking link generated automatically.",
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                title: "CSV Analytics Upload",
                desc: "Publishers upload Steam UTM analytics weekly for transparent tracking.",
              },
              {
                icon: <Shield className="w-6 h-6" />,
                title: "Trusted & Transparent",
                desc: "Both sides see the same data. No hidden metrics or black-box algorithms.",
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: "Global Reach",
                desc: "Connect with influencers and publishers from around the world.",
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: "Quick Setup",
                desc: "Create a campaign or join one in minutes. No complex onboarding.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 hover:border-brand-500/30 transition-all"
              >
                <div className="w-10 h-10 bg-brand-500/10 rounded-lg flex items-center justify-center text-brand-400 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Meet the Team</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              The people behind referred.games, building the bridge between game publishers and influencers.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            {[
              {
                name: "Samuel Karatas",
                role: "Software",
                photo: "/samuel_photo.png",
                linkedin: "https://www.linkedin.com/in/samuel-karatas/",
              },
              {
                name: "Simon Karatas",
                role: "Marketing",
                photo: "/simon_photo.jpeg",
                linkedin: "https://www.linkedin.com/in/simon-karataş-7a8365a8/",
              },
            ].map((member) => (
              <div
                key={member.name}
                className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-8 text-center w-full sm:w-72"
              >
                <div className="w-28 h-28 rounded-full bg-gray-800 border-2 border-gray-700 mx-auto mb-5 overflow-hidden">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold mb-1">{member.name}</h3>
                <p className="text-sm text-gray-400 mb-4">{member.role}</p>
                <div className="flex items-center justify-center gap-3">
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-brand-400 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-b from-brand-900/50 to-gray-900/50 border border-brand-800/30 rounded-3xl p-12 sm:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand-500/10 via-transparent to-transparent" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
                Join the marketplace that connects game publishers with the influencers who drive real results.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/auth?mode=signup&role=publisher"
                  className="group flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-8 py-3.5 rounded-xl transition-all font-semibold shadow-lg shadow-brand-600/25"
                >
                  Start as Publisher
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/auth?mode=signup&role=influencer"
                  className="group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-8 py-3.5 rounded-xl transition-all font-semibold border border-white/10 hover:border-white/20"
                >
                  Start as Influencer
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Gamepad2 className="w-6 h-6 text-brand-400" />
            <span className="text-lg font-bold bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
              referred.games
            </span>
          </div>
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} referred.games. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
